import type {
  TrackingConsentBanner,
  TrackingConsentSettingsButtonPosition,
  TrackingConsentSettingsButtonVariant
} from './types';

const ROOT_CLASS = 'gc-tracking-consent';
const CHOICE_CLASS = `${ROOT_CLASS}__choice`;
const SETTINGS_CLASS = `${ROOT_CLASS}__settings`;
const SVG_NS = 'http://www.w3.org/2000/svg';

type TrackingConsentBannerOptions = {
  banner: TrackingConsentBanner;
  document: Document;
  id: string;
  initialError: string | null;
  showSettingsButton: boolean;
  settingsButtonPosition: TrackingConsentSettingsButtonPosition;
  settingsButtonVariant: TrackingConsentSettingsButtonVariant;
  onAllow: () => void;
  onReject: () => void;
};

export interface TrackingConsentBannerController {
  open(): void;
  showError(message: string): void;
  destroy(): void;
}

const createTextElement = (document: Document, tagName: string, className: string, text: string): HTMLElement => {
  const element = document.createElement(tagName);
  element.className = className;
  element.textContent = text;
  return element;
};

// A self-contained cookie glyph, built as DOM (no innerHTML) to match the
// library's markup-free construction. Decorative: the button carries the label.
const createSettingsIcon = (document: Document): SVGSVGElement => {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', `${SETTINGS_CLASS}-icon`);
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '20');
  svg.setAttribute('height', '20');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const cookie = document.createElementNS(SVG_NS, 'path');
  cookie.setAttribute(
    'd',
    'M12 2a10 10 0 1 0 10 10 1 1 0 0 0-1.31-.95 3 3 0 0 1-3.74-3.74A1 1 0 0 0 16 6a3 3 0 0 1-3-3 1 1 0 0 0-1-1Zm-3 7a1 1 0 1 1-1 1 1 1 0 0 1 1-1Zm6 4a1 1 0 1 1-1 1 1 1 0 0 1 1-1Zm-5 2a1 1 0 1 1-1 1 1 1 0 0 1 1-1Z'
  );
  svg.append(cookie);

  return svg;
};

const isSafePolicyUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' ||
      (url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1'))
    );
  } catch {
    return false;
  }
};

export const createTrackingConsentBanner = ({
  banner,
  document,
  id,
  initialError,
  showSettingsButton,
  settingsButtonPosition,
  settingsButtonVariant,
  onAllow,
  onReject
}: TrackingConsentBannerOptions): TrackingConsentBannerController => {
  let dialog: HTMLElement | null = null;
  let error: HTMLElement | null = null;
  let previouslyFocusedElement: HTMLElement | null = null;
  let settingsButton: HTMLButtonElement | null = null;

  const showError = (message: string): void => {
    if (!dialog) {
      open();
    }

    if (!error && dialog) {
      error = createTextElement(document, 'p', `${ROOT_CLASS}__error`, message);
      error.setAttribute('role', 'alert');
      dialog.append(error);
      return;
    }

    if (error) {
      error.textContent = message;
    }
  };

  const createChoice = (decision: 'allowed' | 'rejected', label: string, handler: () => void): HTMLButtonElement => {
    const button = createTextElement(document, 'button', CHOICE_CLASS, label) as HTMLButtonElement;
    button.type = 'button';
    button.dataset.consentChoice = decision;
    button.addEventListener('click', handler);
    return button;
  };

  const open = (): void => {
    if (dialog) {
      dialog.querySelector<HTMLButtonElement>('[data-consent-choice="allowed"]')?.focus();
      return;
    }

    previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const titleId = `${id}-title`;
    const descriptionId = `${id}-description`;
    dialog = document.createElement('section');
    dialog.className = `${ROOT_CLASS}__dialog`;
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-labelledby', titleId);
    dialog.setAttribute('aria-describedby', descriptionId);
    // aria-modal="false": consent notice is non-blocking — screen readers may still navigate the page behind it.
    dialog.setAttribute('aria-modal', 'false');

    const copy = document.createElement('div');
    copy.className = `${ROOT_CLASS}__copy`;
    const title = createTextElement(document, 'h2', `${ROOT_CLASS}__title`, banner.title);
    title.id = titleId;
    const description = createTextElement(document, 'p', `${ROOT_CLASS}__description`, banner.description);
    description.id = descriptionId;
    copy.append(title, description);

    if (banner.policy_url && isSafePolicyUrl(banner.policy_url)) {
      const policyLink = createTextElement(
        document,
        'a',
        `${ROOT_CLASS}__policy-link`,
        banner.policy_link_label ?? 'Privacy and cookie policy'
      ) as HTMLAnchorElement;
      policyLink.href = banner.policy_url;
      policyLink.target = '_blank';
      policyLink.rel = 'noopener noreferrer';
      copy.append(policyLink);
    }

    const actions = document.createElement('div');
    actions.className = `${ROOT_CLASS}__actions`;
    const allowButton = createChoice('allowed', banner.allow_label, onAllow);
    const rejectButton = createChoice('rejected', banner.reject_label, onReject);
    actions.append(allowButton, rejectButton);
    dialog.append(copy, actions);
    document.body.append(dialog);

    if (initialError) {
      showError(initialError);
    }

    allowButton.focus();
  };

  const destroy = (): void => {
    dialog?.remove();
    dialog = null;
    error = null;

    settingsButton?.remove();
    settingsButton = null;

    if (previouslyFocusedElement?.isConnected) {
      previouslyFocusedElement.focus();
    }
    previouslyFocusedElement = null;
  };

  if (showSettingsButton) {
    const isIcon = settingsButtonVariant === 'icon';
    const className = `${SETTINGS_CLASS} ${SETTINGS_CLASS}--${settingsButtonPosition}${
      isIcon ? ` ${SETTINGS_CLASS}--icon` : ''
    }`;

    if (isIcon) {
      settingsButton = document.createElement('button');
      settingsButton.className = className;
      settingsButton.setAttribute('aria-label', banner.settings_label);
      settingsButton.title = banner.settings_label;
      settingsButton.append(createSettingsIcon(document));
    } else {
      settingsButton = createTextElement(document, 'button', className, banner.settings_label) as HTMLButtonElement;
    }

    settingsButton.type = 'button';
    settingsButton.dataset.consentSettings = '';
    settingsButton.addEventListener('click', open);
    document.body.append(settingsButton);
  }

  return { open, showError, destroy };
};
